import React, { useState } from 'react';
import { Box, Button, FormControl, Link, MenuItem, Select, Stack, Typography } from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSelector } from 'react-redux';
import { apiSettingsCardSx } from '../../../constants/apiSettingsCardTypography';
import { getApiVersionDocsUrl, SUPPORTED_API_VERSIONS } from '../../../constants/apiVersions';
import { useGetApiVersionSettingsHandler, useUpdateApiVersionHandler } from '../dataHandlers/apiVersioningHandlers';
import APIVersionConfirmDialog from './APIVersionConfirmDialog';

const APIVersioningCard = () => {
  const org = useSelector((state) => state.org.org);
  const { settings, isLoading } = useGetApiVersionSettingsHandler();
  const [selectedVersion, setSelectedVersion] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const currentVersion = settings?.active_api_version ?? '';

  const { onSubmit, isPending } = useUpdateApiVersionHandler({
    onSuccess: (_data, variables) => {
      setConfirmOpen(false);
      setSelectedVersion(variables.active_api_version);
    },
  });

  const effectiveSelectedVersion = selectedVersion || currentVersion;
  const apiDocsUrl = getApiVersionDocsUrl(effectiveSelectedVersion);
  const isPartner = Boolean(org?.is_partner);
  const hasChanges = !isLoading && effectiveSelectedVersion !== currentVersion;

  const handleSave = () => {
    if (!hasChanges) return;
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    if (!hasChanges) return;
    onSubmit(effectiveSelectedVersion);
  };

  return (
    <Box sx={apiSettingsCardSx.container}>
      <Stack spacing={1.5}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <AccountTreeOutlinedIcon fontSize="small" color="primary" />
            <Typography sx={apiSettingsCardSx.title}>API Versioning</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={apiSettingsCardSx.description}>
            Select the global version for your API requests and Webhook payloads.
            <br />
            Note: Changing this affects the API contract and your schema validations immediately.
          </Typography>
        </Box>

        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            p: 1.5,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="body2" sx={apiSettingsCardSx.label}>
              Active Version
            </Typography>
            <FormControl fullWidth size="small" disabled={isLoading || isPending}>
              <Select
                value={effectiveSelectedVersion}
                displayEmpty
                sx={apiSettingsCardSx.input}
                renderValue={(value) => {
                  if (!value) {
                    return (
                      <Typography component="span" color="text.secondary" sx={apiSettingsCardSx.input}>
                        No API Version selected
                      </Typography>
                    );
                  }
                  return value;
                }}
                onChange={(event) => setSelectedVersion(event.target.value)}
              >
                {SUPPORTED_API_VERSIONS.map((version) => (
                  <MenuItem key={version} value={version} sx={apiSettingsCardSx.input}>
                    {version}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
          <Link
            href={apiDocsUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            color="primary"
            aria-disabled={!effectiveSelectedVersion}
            sx={{
              ...apiSettingsCardSx.link,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              pointerEvents: effectiveSelectedVersion ? 'auto' : 'none',
              opacity: effectiveSelectedVersion ? 1 : 0.5,
            }}
          >
            View API Documentation
            <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
          </Link>
          <Button
            variant="contained"
            size="small"
            sx={apiSettingsCardSx.button}
            disabled={!hasChanges || isPending || isLoading}
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </Stack>
      </Stack>

      <APIVersionConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        isPending={isPending}
        isPartner={isPartner}
      />
    </Box>
  );
};

export default APIVersioningCard;
